import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Phone, Mail, MapPin, ChevronDown, Linkedin, Instagram, ArrowUp, MessageSquare, Download } from "lucide-react";
import { OFFICES, EMAILS, LICENSE, SERVICES } from "../data/content";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/services", label: "Services" },
  { to: "/partners", label: "Partners" },
  { to: "/team", label: "Leadership" },
  { to: "/contact", label: "Contact" },
];

export const Brand = ({ light = true, showTagline = true }) => (
  <Link to="/" className="flex items-center gap-3 group" data-testid="brand-home-link">
    <img src="/assets/logo-mark.png" alt="Vardhamaan Consultancy Services logo" className="h-11 sm:h-12 w-auto drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)] transition-transform duration-300 group-hover:scale-105" data-testid="brand-logo" />
    <span className={`leading-tight ${light ? "text-white" : "text-[#2A0E20]"}`}>
      <span className="block font-heading font-extrabold text-[15px] sm:text-[16px] tracking-wide uppercase">Vardhamaan</span>
      <span className={`block text-[10px] sm:text-[11px] font-semibold tracking-[0.28em] uppercase ${light ? "text-[#D4AF37]" : "text-[#811F5D]"}`}>Consultancy Services</span>
      {showTagline && (
        <span className={`block text-[9px] font-medium tracking-wide mt-0.5 ${light ? "text-[#D4AF37]/80" : "text-[#811F5D]/75"}`}>
          Trusted partner in your success
        </span>
      )}
    </span>
  </Link>
);

const navLinkCls = (isActive) =>
  `relative text-sm font-semibold tracking-wide transition-all duration-300 py-1 px-1 ${
    isActive ? "text-[#D4AF37]" : "text-white/80 hover:text-white"
  } after:absolute after:left-0 after:bottom-0 after:h-[2px] after:bg-[#D4AF37] after:rounded-full after:transition-all after:duration-300 ${
    isActive ? "after:w-full" : "after:w-0 hover:after:w-full"
  }`;

const DesktopNavigation = () => (
  <nav className="hidden lg:flex items-center gap-7 xl:gap-8" data-testid="desktop-navigation">
    {NAV.map((item) => item.label === "Services" ? (
      <div key={item.to} className="relative group">
        <NavLink
          to={item.to}
          data-testid="nav-link-services"
          className={({ isActive }) => `${navLinkCls(isActive)} inline-flex items-center gap-1.5`}
          aria-haspopup="true"
        >
          {item.label} <ChevronDown size={14} className="transition-transform duration-300 group-hover:rotate-180 text-[#D4AF37]" />
        </NavLink>
        <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300 ease-out" data-testid="services-dropdown">
          <div className="w-84 bg-[#3A0D2A]/95 backdrop-blur-2xl border border-[#D4AF37]/25 shadow-2xl rounded-2xl p-2.5">
            <div className="h-[2px] bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent mb-2 rounded-full" />
            {SERVICES.map((service) => (
              <Link
                key={service.slug}
                to={`/services#${service.slug}`}
                className="flex items-center gap-3 px-3.5 py-2.5 text-sm font-medium text-white/90 rounded-xl transition-all duration-200 hover:bg-[#811F5D] hover:text-[#D4AF37] hover:pl-5 group/item"
                data-testid={`services-dropdown-${service.slug}`}
              >
                <span className="font-mono text-[11px] text-[#D4AF37] font-bold group-hover/item:scale-110 transition-transform">{service.num}</span>
                <span className="truncate">{service.title}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    ) : (
      <NavLink key={item.to} to={item.to} data-testid={`nav-link-${item.label.toLowerCase().replaceAll(" ", "-")}`} className={({ isActive }) => navLinkCls(isActive)}>
        {item.label}
      </NavLink>
    ))}
  </nav>
);

const MobileNavigation = ({ open, setOpen }) => (
  <AnimatePresence>
    {open && (
      <motion.nav
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: "auto", opacity: 1 }}
        exit={{ height: 0, opacity: 0 }}
        transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
        className="lg:hidden overflow-hidden bg-[#2A0E20]/98 backdrop-blur-2xl border-t border-white/10 shadow-2xl"
        data-testid="mobile-navigation"
      >
        <div className="px-6 py-5 flex flex-col gap-1.5">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setOpen(false)}
              data-testid={`mobile-nav-${item.label.toLowerCase().replaceAll(" ", "-")}`}
              className={({ isActive }) =>
                `flex items-center justify-between py-3 px-3 rounded-xl text-base font-semibold transition-all ${
                  isActive
                    ? "bg-[#811F5D]/40 text-[#D4AF37] border-l-4 border-[#D4AF37]"
                    : "text-white/85 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <span>{item.label}</span>
              <ArrowUpRight size={16} className="opacity-40" />
            </NavLink>
          ))}
          <div className="pt-4 mt-2 border-t border-white/10 flex flex-col gap-3">
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="btn-shine w-full text-center py-3.5 bg-[#D4AF37] text-[#4A1136] font-bold text-sm tracking-wide rounded-xl shadow-lg"
            >
              Hire Workforce
            </Link>
            <a
              href="tel:+918446497833"
              className="flex items-center justify-center gap-2 py-2.5 text-xs font-semibold text-white/80 hover:text-[#D4AF37] bg-white/5 rounded-xl border border-white/10"
            >
              <Phone size={14} className="text-[#D4AF37]" /> +91 84464 97833 (Mumbai)
            </a>
          </div>
        </div>
      </motion.nav>
    )}
  </AnimatePresence>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ease-out border-b ${
        scrolled
          ? "bg-[#2A0E20]/90 backdrop-blur-xl border-[#D4AF37]/20 shadow-xl shadow-black/30 py-1.5"
          : "bg-gradient-to-b from-[#2A0E20]/80 via-[#2A0E20]/40 to-transparent border-transparent py-3"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[68px] sm:h-[72px] flex items-center justify-between">
        <Brand />
        <DesktopNavigation />
        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            className="btn-shine hidden sm:inline-flex items-center gap-2 bg-[#D4AF37] text-[#4A1136] px-5 py-2.5 text-sm font-bold tracking-wide transition-all duration-300 hover:bg-white hover:text-[#811F5D] hover:gap-3 active:scale-[0.97] shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-[#811F5D] focus:ring-offset-2 rounded-xl"
            data-testid="header-cta-link"
          >
            Hire Workforce <ArrowUpRight size={16} />
          </Link>
          <button
            className="lg:hidden text-white p-2 rounded-lg hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
      <MobileNavigation open={open} setOpen={setOpen} />
    </header>
  );
};

export const Footer = () => (
  <footer className="bg-[#2A0E20] text-white/70 relative border-t border-[#D4AF37]/15" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid gap-12 md:grid-cols-3">
      <div>
        <Brand />
        <p className="mt-5 text-sm leading-relaxed max-w-xs text-white/75">Trusted partner in your success. ISO-certified, MEA-registered global recruitment and workforce solutions since the early 2000s.</p>
        <p className="mt-4 text-xs font-mono text-[#D4AF37]/90 font-semibold">License: {LICENSE}</p>
        <div className="mt-5">
          <a
            href="/assets/VARDHAMAAN CONSULTANCY SERVICES.pdf"
            download="VARDHAMAAN CONSULTANCY SERVICES.pdf"
            className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-white text-[#4A1136] hover:text-[#811F5D] px-4 py-2 text-xs font-bold tracking-wider uppercase transition-all duration-300 active:scale-[0.97] rounded-xl shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-[#811F5D] focus:ring-offset-2"
            data-testid="download-profile-btn"
          >
            <Download size={14} />
            <span>Download Company Profile</span>
          </a>
        </div>
        <div className="mt-6 flex items-center gap-3">
          <a
            href="https://www.linkedin.com/company/vardhamaan-counsultancy-services/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center w-9 h-9 rounded-xl bg-white/10 hover:bg-[#D4AF37] hover:text-[#4A1136] text-white transition-all duration-300 active:scale-[0.95] shadow"
            aria-label="LinkedIn"
            data-testid="social-linkedin"
          >
            <Linkedin size={17} />
          </a>
          <a
            href="https://www.instagram.com/vardhamaan_consultancy_services/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center w-9 h-9 rounded-xl bg-white/10 hover:bg-[#D4AF37] hover:text-[#4A1136] text-white transition-all duration-300 active:scale-[0.95] shadow"
            aria-label="Instagram"
            data-testid="social-instagram"
          >
            <Instagram size={17} />
          </a>
        </div>
      </div>
      <div>
        <h4 className="font-heading font-bold text-white text-sm uppercase tracking-[0.2em] mb-5">Explore</h4>
        <ul className="space-y-3 text-sm">
          {NAV.map((item) => (
            <li key={item.to}>
              <Link to={item.to} className="hover:text-[#D4AF37] transition-colors duration-200" data-testid={`footer-link-${item.label.toLowerCase().replaceAll(" ", "-")}`}>{item.label}</Link>
            </li>
          ))}
          <li className="pt-1">
            <Link to="/privacy" className="hover:text-[#D4AF37] transition-colors duration-200 text-xs font-mono text-[#D4AF37]/90 inline-flex items-center gap-1.5" data-testid="footer-link-dpdp-privacy">
              <span>DPDP & Privacy Notice</span>
            </Link>
          </li>
        </ul>
      </div>
      <div>
        <h4 className="font-heading font-bold text-white text-sm uppercase tracking-[0.2em] mb-5">Head Office</h4>
        <p className="flex gap-2 text-sm leading-relaxed text-white/80"><MapPin size={16} className="shrink-0 mt-1 text-[#D4AF37]" />{OFFICES[0].address}</p>
        <p className="flex gap-2 text-sm mt-3 items-center text-white/80"><Phone size={15} className="text-[#D4AF37]" />{OFFICES[0].phone} · +974 5562 1384</p>
        {EMAILS.map((e) => (
          <a key={e} href={`mailto:${e}`} className="flex gap-2 text-sm mt-2 items-center hover:text-[#D4AF37] transition-colors break-all text-white/80" data-testid="footer-email-link"><Mail size={15} className="shrink-0 text-[#D4AF37]" />{e}</a>
        ))}
      </div>
    </div>
    <div className="border-t border-white/10 py-5 text-center text-xs tracking-wide text-white/50 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
      <span>© 2026 Vardhamaan Consultancy Services · Mumbai · Ahmedabad · Jaipur · Doha</span>
      <span className="hidden sm:inline text-white/20">|</span>
      <Link to="/privacy" className="hover:text-[#D4AF37] transition-colors duration-200">DPDP Compliance & Privacy</Link>
      <span className="hidden sm:inline text-white/20">|</span>
      <span>Powered by <a href="https://bugzx.space" target="_blank" rel="noopener noreferrer" className="hover:text-[#D4AF37] transition-colors duration-200 underline">bugzx.space</a></span>
    </div>
  </footer>
);

export const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  const [showScroll, setShowScroll] = useState(false);

  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth", block: "start" }), 850);
      return () => clearTimeout(t);
    }
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname, hash]);

  useEffect(() => {
    const checkScroll = () => setShowScroll(window.scrollY > 400);
    window.addEventListener("scroll", checkScroll, { passive: true });
    return () => window.removeEventListener("scroll", checkScroll);
  }, []);

  const scrollToTopAction = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="fixed bottom-6 right-6 z-40 flex flex-col gap-2.5">
      <Link
        to="/contact"
        aria-label="Quick inquiry"
        className="w-12 h-12 rounded-full bg-[#811F5D] text-white flex items-center justify-center shadow-lg hover:shadow-xl hover:bg-[#4A1136] text-[#D4AF37] hover:scale-105 active:scale-95 transition-all duration-300 border border-[#D4AF37]/40"
        title="Send Manpower Inquiry"
      >
        <MessageSquare size={20} className="text-[#D4AF37]" />
      </Link>
      <AnimatePresence>
        {showScroll && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={scrollToTopAction}
            aria-label="Scroll back to top"
            className="w-12 h-12 rounded-full bg-[#2A0E20]/90 backdrop-blur-md text-[#D4AF37] flex items-center justify-center shadow-lg hover:shadow-xl hover:bg-[#D4AF37] hover:text-[#2A0E20] hover:scale-105 active:scale-95 transition-all duration-300 border border-[#D4AF37]/30"
          >
            <ArrowUp size={20} />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};


export const Page = ({ children, testId }) => (
  <motion.main
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -14 }}
    transition={{ duration: 0.35, ease: "easeOut" }}
    data-testid={testId}
  >
    {children}
  </motion.main>
);

export const PageHero = ({ eyebrow, title, sub, image }) => (
  <section className="bg-[#4A1136] relative overflow-hidden">
    {image && <img src={image} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" />}
    {image && <div className="absolute inset-0 bg-gradient-to-r from-[#3A0D2A]/[0.97] via-[#4A1136]/90 to-[#4A1136]/60" />}
    <div className="absolute inset-0 hero-pattern opacity-40" />
    <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-20 lg:pt-40 lg:pb-28 relative">
      <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="font-mono text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-4">{eyebrow}</motion.p>
      <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }} className="font-heading font-extrabold text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-3xl">{title}</motion.h1>
      {sub && <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="mt-6 text-white/75 text-base md:text-lg max-w-2xl leading-relaxed">{sub}</motion.p>}
    </div>
    <div className="h-1 bg-gradient-to-r from-[#D4AF37] via-[#811F5D] to-[#D4AF37]" />
  </section>
);
